/**
 * Student Portal - Completely Redesigned
 * Mobile-first, responsive, with real data and context-aware AI Coach
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  BookOpen,
  FlaskConical,
  Target,
  MessageCircle,
  FileText,
  Calendar,
  BarChart3,
  Trophy,
  TrendingUp,
  Award,
  Clock,
  Play,
  CheckCircle,
  AlertCircle,
  Eye,
  Download,
  Send,
  Star,
  Zap,
  Search,
  Bell,
  Settings,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Activity,
  Users,
  Video,
  Brain,
  Leaf,
  Filter,
  Plus,
  Share2,
  Bookmark,
  Edit,
  Rocket,
  LogOut,
  Home,
  Sparkles,
  Flame,
  GraduationCap,
  ArrowUpRight,
  CircleDot,
  Timer,
  Medal,
  Lightbulb,
  Info,
  Layers,
  Heart
} from 'lucide-react';

// Context & Services
import { useAuth } from '../../context/AuthContext';
import { AuthApi, EnrollmentApi, VirtualLabApi } from '../../services/api';

// Components
import StudentPortalShell from './StudentPortalShell';
import StudentPortalTabHeader from './StudentPortalTabHeader';
import StudentPortalDashboard from './StudentPortalDashboard';
import {
  filterEnrollments,
  getFocusEnrollment,
  filterEnrollmentsByStatus,
  sortEnrollments,
} from './studentPortalUtils';
import MyCourseCard from './MyCourseCard';
import StudentPortalSettings from './StudentPortalSettings';
import './StudentPortal.css';
import './StudentPortalShell.css';
import './StudentPortalDashboard.css';
import './StudentPortalSettings.css';

// ─── CERTIFICATES TAB (must be a real component to use hooks) ─────────────────
const CertificatesTab = ({ token }) => {
  const navigate = useNavigate();
  const [certList, setCertList] = React.useState([]);
  const [certLoading, setCertLoading] = React.useState(true);
  const [certError, setCertError] = React.useState(null);
  const [downloading, setDownloading] = React.useState(null);
  const [snack, setSnack] = React.useState({ open: false, msg: '' });

  React.useEffect(() => {
    let cancelled = false;
    const fetchCerts = async () => {
      setCertLoading(true);
      setCertError(null);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app'}/api/certificates/my`,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
        const data = await res.json().catch(() => ({}));
        if (!cancelled) {
          if (res.ok) {
            const list = Array.isArray(data) ? data : (data?.certificates || data?.data || []);
            setCertList(list);
          } else {
            setCertError(data?.message || 'Failed to load certificates.');
          }
        }
      } catch {
        if (!cancelled) setCertError('Unable to connect. Please check your connection.');
      } finally {
        if (!cancelled) setCertLoading(false);
      }
    };
    fetchCerts();
    return () => { cancelled = true; };
  }, [token]);

  const handleDownload = async (cert) => {
    const certId = cert._id || cert.id || cert.certificateId;
    if (!certId) return;
    setDownloading(certId);
    try {
      const API = process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app';
      const res = await fetch(`${API}/api/certificates/download/${certId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificate-${certId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setSnack({ open: true, msg: 'Certificate downloaded!' });
    } catch {
      setSnack({ open: true, msg: 'Download failed. Please try again.' });
    } finally {
      setDownloading(null);
    }
  };

  const handleShare = (cert) => {
    const certId = cert._id || cert.id || cert.certificateId;
    const url = `${window.location.origin}/verify/${certId}`;
    navigator.clipboard.writeText(url)
      .then(() => setSnack({ open: true, msg: 'Verification link copied!' }))
      .catch(() => setSnack({ open: true, msg: `Link: ${url}` }));
  };

  const formatDate = (d) => {
    if (!d) return 'N/A';
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="tab-content">
      <StudentPortalTabHeader
        icon={<Award size={20} />}
        title="My Certificates"
        subtitle="Your earned credentials — download, share, or verify"
        actions={certList.length > 0 ? (
          <span className="badge badge-success" style={{ fontSize: '0.875rem', padding: '6px 14px' }}>
            {certList.length} Earned
          </span>
        ) : null}
      />

      {snack.open && (
        <div
          style={{
            position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
            background: '#09090b', color: '#fafafa', borderRadius: 8, padding: '12px 20px',
            border: '1px solid rgba(255,255,255,0.08)',
            zIndex: 9999, boxShadow: '0 8px 32px rgba(0,0,0,0.25)', fontSize: '0.875rem', fontWeight: 500,
          }}
          onClick={() => setSnack({ open: false, msg: '' })}
        >
          {snack.msg}
        </div>
      )}

      {certLoading ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <div className="skeleton skeleton-card" style={{ height: 120, marginBottom: 16 }} />
          <div className="skeleton skeleton-card" style={{ height: 120, marginBottom: 16 }} />
          <div className="skeleton skeleton-card" style={{ height: 120 }} />
        </div>
      ) : certError ? (
        <div className="portal-card" style={{ borderLeft: '4px solid #ef4444', padding: '20px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#ef4444', marginBottom: 8, fontWeight: 600 }}>
            ⚠ {certError}
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => { setCertError(null); setCertLoading(true); }}>
            Retry
          </button>
        </div>
      ) : certList.length === 0 ? (
        <div className="portal-card">
          <div className="empty-state">
            <div className="empty-icon">🎓</div>
            <h3 className="empty-title">No certificates yet</h3>
            <p className="empty-description">
              Complete a course to earn your first certificate. Every milestone brings you closer to your goals!
            </p>
            <button className="btn btn-primary" onClick={() => navigate('/courses')}>
              Browse Courses
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
          {certList.map((cert, i) => {
            const certId = cert._id || cert.id || cert.certificateId;
            const courseName = cert.courseName || cert.courseTitle || cert.course?.title || 'Unknown Course';
            const completedAt = cert.completedAt || cert.issuedAt || cert.createdAt;
            return (
              <div key={certId || i} className="portal-card" style={{ padding: 0, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)', transition: 'transform 0.2s, box-shadow 0.2s' }}>
                <div style={{ height: 5, background: 'linear-gradient(90deg, #7c3aed, #3b82f6)' }} />
                <div style={{ padding: '20px 20px 0' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg, #7c3aed, #3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Award size={20} color="white" />
                      </div>
                      <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '3px 8px', borderRadius: 4, border: '1px solid rgba(16,185,129,0.2)' }}>✓ VERIFIED</span>
                    </div>
                  </div>
                  <h3 style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 6, lineHeight: 1.4 }}>{courseName}</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, color: '#71717a', fontSize: '0.8rem' }}>
                    <span>📅</span><span>Completed {formatDate(completedAt)}</span>
                  </div>
                  {certId && (
                    <div style={{ fontSize: '0.7rem', color: '#a1a1aa', fontFamily: 'monospace', marginBottom: 4 }}>
                      ID: {String(certId).slice(0, 24)}…
                    </div>
                  )}
                </div>
                <div style={{ padding: '16px 20px 20px', display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button className="btn btn-primary btn-sm" onClick={() => handleDownload(cert)} disabled={downloading === certId} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    {downloading === certId ? <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> : <Download size={14} />}
                    {downloading === certId ? 'Downloading…' : 'Download PDF'}
                  </button>
                  <button className="btn btn-secondary btn-sm btn-icon" onClick={() => handleShare(cert)} title="Copy verification link" style={{ padding: '8px 10px' }}><Share2 size={14} /></button>
                  <button className="btn btn-secondary btn-sm btn-icon" onClick={() => navigate(`/verify/${certId}`)} title="View public certificate" style={{ padding: '8px 10px' }}><Eye size={14} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const StudentPortal = () => {
  const navigate = useNavigate();
  const { token, user, logout } = useAuth();

  // Core State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [refreshKey, setRefreshKey] = useState(0);

  // UI State
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCourseId, setExpandedCourseId] = useState(null);
  const [courseFilter, setCourseFilter] = useState('all');
  const [courseSort, setCourseSort] = useState('recent');

  // Virtual Labs State
  const [virtualLabs, setVirtualLabs] = useState([]);
  const [labsLoading, setLabsLoading] = useState(false);
  const [generatingLabKey, setGeneratingLabKey] = useState(null);

  // Responsive check
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(false);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load data
  const loadData = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const meRes = await AuthApi.me(token);
      const userProfile = meRes.data?.user || meRes.user;
      setProfile(userProfile);

      const enrRes = await EnrollmentApi.listMine(token, { limit: 1000 });
      const backendEnrollments = enrRes.data?.enrollments || enrRes.enrollments || [];
      const validEnrollments = backendEnrollments.filter(e => e.course && e.course.title);
      setEnrollments(validEnrollments);

      // Load virtual labs with access status
      try {
        const labsRes = await VirtualLabApi.getLabsWithAccess(token);
        if (labsRes.success) {
          setVirtualLabs(labsRes.data);
        }
      } catch (labErr) {
        // Labs API not available — static data will be used as fallback
      }
    } catch (e) {
      console.error('Error loading data:', e);
      setError(e.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshKey]);

  // Check refresh flag
  useEffect(() => {
    const checkRefresh = () => {
      const flag = localStorage.getItem('portalShouldRefresh');
      if (flag === 'true') {
        localStorage.removeItem('portalShouldRefresh');
        setRefreshKey(k => k + 1);
      }
    };
    checkRefresh();
    const handleVisibility = () => { if (!document.hidden) checkRefresh(); };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Computed values
  const stats = useMemo(() => {
    const completed = enrollments.filter(e => e.status === 'completed').length;
    const inProgress = enrollments.filter(e => e.status !== 'completed').length;
    const totalHours = Math.round(enrollments.reduce((sum, e) => 
      sum + (e.analytics?.totalVideoWatched || 0), 0) * 10) / 10;
    const avgProgress = enrollments.length > 0
      ? Math.round(enrollments.reduce((sum, e) => 
          sum + (e.progress?.percentageComplete || 0), 0) / enrollments.length)
      : 0;
    return {
      coursesCompleted: completed,
      coursesInProgress: inProgress,
      totalCourses: enrollments.length,
      totalHours,
      averageProgress: avgProgress,
      achievementPoints: enrollments.length * 100 + completed * 250,
      currentStreak: 7 // Would come from API
    };
  }, [enrollments]);

  const studentInfo = useMemo(() => ({
    name: profile?.name || user?.name || 'Student',
    email: profile?.email || user?.email || '',
    avatar: (profile?.name || user?.name || 'S U').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase(),
    program: profile?.jobTitle || profile?.program || 'QoreAcademy Learner',
  }), [profile, user]);

  const handleProfileUpdated = useCallback((updatedUser) => {
    setProfile(updatedUser);
    try {
      const saved = localStorage.getItem('authUser');
      if (saved) {
        const parsed = JSON.parse(saved);
        localStorage.setItem('authUser', JSON.stringify({ ...parsed, ...updatedUser }));
      }
    } catch { /* ignore */ }
  }, []);

  // AI Context for the coach
  const aiContext = useMemo(() => ({
    currentPage: activeTab,
    pageContext: getPageContext(activeTab),
    user: {
      name: studentInfo.name,
      email: studentInfo.email,
      program: studentInfo.program
    },
    stats,
    enrollments: enrollments.map(e => ({
      courseTitle: e.course?.title,
      courseId: e.course?._id,
      progress: e.progress?.percentageComplete || 0,
      status: e.status,
      instructor: e.course?.instructor?.name
    }))
  }), [activeTab, studentInfo, stats, enrollments]);

  // Store context for global AI Tutor to read
  useEffect(() => {
    try {
      localStorage.setItem('aiTutorContext', JSON.stringify(aiContext));
    } catch (e) {
      // Ignore storage errors
    }
  }, [aiContext]);

  // Helpers
  const getProgress = (enr) => Math.max(0, Math.min(100, 
    enr?.progress?.percentageComplete ?? enr?.progressPercentage ?? 0));

  const colorPalette = ['#f59e0b', '#8b5cf6', '#10b981', '#3b82f6', '#ef4444', '#06b6d4', '#ec4899'];
  const pickColor = (key = '') => {
    let h = 0;
    for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
    return colorPalette[Math.abs(h) % colorPalette.length];
  };

  const getCategoryIcon = (cat = '') => {
    const c = cat.toLowerCase();
    if (c.includes('ai') || c.includes('machine')) return <Brain size={20} />;
    if (c.includes('sustain') || c.includes('green')) return <Leaf size={20} />;
    if (c.includes('analytics') || c.includes('data')) return <BarChart3 size={20} />;
    return <BookOpen size={20} />;
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (isMobile) setSidebarOpen(false);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
      const map = { 1: 'dashboard', 2: 'courses', 3: 'learningPath', 4: 'labs', 5: 'progress' };
      if (map[e.key]) handleTabChange(map[e.key]);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMobile]);

  const refresh = () => setRefreshKey(k => k + 1);

  const TAB_META = {
    dashboard: { title: 'Dashboard', subtitle: 'Your learning command center — progress, momentum, and next steps.' },
    learningPath: { title: 'Learning Path', subtitle: 'AI-personalized roadmap aligned to your skills and career goals.' },
    courses: { title: 'My Courses', subtitle: 'Continue enrolled courses and track completion.' },
    labs: { title: 'Virtual Labs', subtitle: 'Hands-on practice in simulated engineering environments.' },
    assignments: { title: 'Assignments', subtitle: 'Homework, quizzes, and deliverables across your courses.' },
    schedule: { title: 'Schedule', subtitle: 'Sessions, deadlines, and learning milestones.' },
    progress: { title: 'Progress', subtitle: 'Deep analytics on study time, completion, and growth.' },
    achievements: { title: 'Achievements', subtitle: 'Badges, streaks, and milestones you have unlocked.' },
    certificates: { title: 'Certificates', subtitle: 'Download, share, and verify your earned credentials.' },
    messages: { title: 'Messages', subtitle: 'Instructor updates and community conversations.' },
    resources: { title: 'Resources', subtitle: 'Guides, downloads, and reference materials.' },
    settings: { title: 'Settings', subtitle: 'Profile, learning preferences, notifications, and security.' },
  };

  const navGroups = [
    {
      label: 'Overview',
      items: [{ id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} /> }],
    },
    {
      label: 'Learn',
      items: [
        { id: 'learningPath', label: 'Learning Path', icon: <Target size={18} />, badge: 'AI' },
        { id: 'courses', label: 'My Courses', icon: <BookOpen size={18} />, count: stats.totalCourses },
        { id: 'labs', label: 'Virtual Labs', icon: <FlaskConical size={18} /> },
        { id: 'assignments', label: 'Assignments', icon: <FileText size={18} /> },
      ],
    },
    {
      label: 'Track',
      items: [
        { id: 'schedule', label: 'Schedule', icon: <Calendar size={18} /> },
        { id: 'progress', label: 'Progress', icon: <BarChart3 size={18} /> },
        { id: 'achievements', label: 'Achievements', icon: <Trophy size={18} /> },
        { id: 'certificates', label: 'Certificates', icon: <Award size={18} /> },
      ],
    },
    {
      label: 'Connect',
      items: [
        { id: 'messages', label: 'Messages', icon: <MessageCircle size={18} /> },
        { id: 'resources', label: 'Resources', icon: <FileText size={18} /> },
      ],
    },
  ];

  const activeTabMeta = TAB_META[activeTab] || TAB_META.dashboard;
  const filteredEnrollments = useMemo(() => {
    const searched = filterEnrollments(enrollments, searchTerm);
    const statusFiltered = filterEnrollmentsByStatus(searched, courseFilter);
    return sortEnrollments(statusFiltered, courseSort);
  }, [enrollments, searchTerm, courseFilter, courseSort]);
  const focusEnrollment = useMemo(() => getFocusEnrollment(enrollments), [enrollments]);

  const renderEmptyRich = (icon, title, description, primary, secondary) => (
    <div className="portal-card">
      <div className="sp-empty-rich">
        <div className="sp-empty-rich__icon">{icon}</div>
        <h3>{title}</h3>
        <p>{description}</p>
        <div className="sp-empty-rich__actions">
          {primary}
          {secondary}
        </div>
      </div>
    </div>
  );

  const renderDashboard = () => (
    <StudentPortalDashboard
      studentInfo={studentInfo}
      stats={stats}
      enrollments={enrollments}
      searchTerm={searchTerm}
      onTabChange={handleTabChange}
      getCategoryIcon={getCategoryIcon}
      pickColor={pickColor}
    />
  );

  const renderCourses = () => (
    <div className="tab-content">
      <StudentPortalTabHeader
        icon={<BookOpen size={20} />}
        title={TAB_META.courses.title}
        subtitle={TAB_META.courses.subtitle}
        actions={(
          <>
            <button className="btn btn-secondary btn-sm" onClick={refresh}>
              <Activity size={16} />
              Refresh
            </button>
            <span className="badge badge-warning">{enrollments.length} enrolled</span>
          </>
        )}
      />

      {enrollments.length > 0 && (
        <div className="my-courses-toolbar">
          <div className="my-courses-filters" role="tablist" aria-label="Filter courses">
            {[
              { id: 'all', label: 'All', count: enrollments.length },
              { id: 'active', label: 'In progress', count: stats.coursesInProgress },
              { id: 'completed', label: 'Completed', count: stats.coursesCompleted },
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={courseFilter === f.id}
                className={`my-courses-filter ${courseFilter === f.id ? 'active' : ''}`}
                onClick={() => setCourseFilter(f.id)}
              >
                {f.label}
                <span className="my-courses-filter__count">{f.count}</span>
              </button>
            ))}
          </div>
          <label className="my-courses-sort">
            <span>Sort</span>
            <select value={courseSort} onChange={(e) => setCourseSort(e.target.value)}>
              <option value="recent">Recently active</option>
              <option value="progress">Progress</option>
              <option value="title">Title A–Z</option>
            </select>
          </label>
        </div>
      )}

      {searchTerm.trim() && (
        <p className="sp-search-callout" style={{ marginBottom: '1rem' }}>
          Showing {filteredEnrollments.length} result{filteredEnrollments.length !== 1 ? 's' : ''} for “{searchTerm}”
        </p>
      )}

      {enrollments.length === 0 ? (
        renderEmptyRich(
          '📚',
          'Build your curriculum',
          'Courses you enroll in appear here with progress, lesson tracking, and one-click resume.',
          <button type="button" className="btn btn-primary" onClick={() => navigate('/courses')}>Browse courses</button>,
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/create-path')}>Create AI path</button>
        )
      ) : filteredEnrollments.length === 0 ? (
        renderEmptyRich(
          '🔍',
          'No matches',
          `Nothing enrolled matches “${searchTerm}”. Try another keyword or clear search.`,
          <button type="button" className="btn btn-primary" onClick={() => setSearchTerm('')}>Clear search</button>,
          null
        )
      ) : (
        <div className="my-courses-grid">
          {filteredEnrollments.map((enrollment) => {
            const color = pickColor(enrollment.course?.category || enrollment.course?.title || '');
            const isExpanded = expandedCourseId === enrollment._id;
            return (
              <MyCourseCard
                key={enrollment._id}
                enrollment={enrollment}
                color={color}
                categoryIcon={getCategoryIcon(enrollment.course?.category)}
                isExpanded={isExpanded}
                onToggleDetails={() => setExpandedCourseId(isExpanded ? null : enrollment._id)}
                onContinue={() => navigate(`/studentportal/mycourses/${enrollment.course._id}`)}
              />
            );
          })}
        </div>
      )}
    </div>
  );

  const renderLabs = () => {
    // Get static labs if API didn't return any
    const labsToShow = (virtualLabs.length > 0 ? virtualLabs : [
      {
        labId: 'semicon-mech',
        title: 'Semiconductor Mechanical Studio',
        description: 'Warpage, peel/shear, and an AI packaging-senior defence',
        category: 'manufacturing',
        difficulty: 'Intermediate',
        status: 'active',
        color: '#22d3ee',
        isFreeAccess: false,
        userAccess: { isUnlocked: true, accessLevel: 'full' }
      },
      {
        labId: 'physics',
        title: 'Engineering Physics Playground',
        description: 'Forces, energy, spin, shake, flow, heat — physics you can fiddle with',
        category: 'physics',
        difficulty: 'Intermediate',
        status: 'coming-soon',
        color: '#8b5cf6',
        isFreeAccess: false,
        userAccess: { isUnlocked: false, canGenerateKey: false }
      },
      {
        labId: 'process-engineering-sim',
        title: 'Advanced Process Engineering Simulator',
        description: 'Real-time industrial process simulation with AI optimization',
        category: 'process',
        difficulty: 'Advanced',
        status: 'coming-soon',
        color: '#ca8a04',
        isFreeAccess: false,
        userAccess: { isUnlocked: false, canGenerateKey: false }
      }
    ]).filter((lab) => lab.labId !== 'medical-statistics' && lab.labId !== 'medstatistics');

    const handleGenerateKey = async (lab, enrollmentId) => {
      setGeneratingLabKey(lab.labId);
      try {
        const response = await VirtualLabApi.generateKey(token, lab.labId, enrollmentId);
        if (response.success) {
          alert(`Lab Key Generated: ${response.data.key}\n\nSave this key to access ${lab.title}`);
          // Refresh labs
          const labsRes = await VirtualLabApi.getLabsWithAccess(token);
          if (labsRes.success) setVirtualLabs(labsRes.data);
        }
      } catch (err) {
        alert(err.message || 'Failed to generate key');
      } finally {
        setGeneratingLabKey(null);
      }
    };

    const handleLaunchLab = (lab) => {
      if (lab.labId === 'physics' || lab.labId === 'physics-mechanics' || lab.labId === 'medphysics') {
        navigate('/virtuallabs/physics');
      } else if (lab.labId === 'semicon-mech') {
        navigate('/virtuallabs/semicon-mech');
      } else if (lab.userAccess?.isUnlocked || lab.isFreeAccess) {
        navigate(`/virtuallabs/${lab.labId}`);
      } else {
        navigate('/virtuallabs');
      }
    };

    return (
      <div className="tab-content">
        <StudentPortalTabHeader
          icon={<FlaskConical size={20} />}
          title={TAB_META.labs.title}
          subtitle={TAB_META.labs.subtitle}
          actions={(
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/virtuallabs')}>
              <Eye size={16} />
              View All Labs
            </button>
          )}
        />

        <div className="courses-grid">
          {labsToShow.slice(0, 6).map(lab => {
            const isUnlocked = lab.userAccess?.isUnlocked || lab.isFreeAccess;
            const canGenerate = lab.userAccess?.canGenerateKey && !isUnlocked;
            
            return (
              <div 
                key={lab.labId} 
                className="portal-card interactive"
                style={{ opacity: isUnlocked ? 1 : 0.85 }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div 
                    className="course-icon" 
                    style={{ 
                      background: `${lab.color}20`, 
                      color: lab.color 
                    }}
                  >
                    <FlaskConical size={20} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold flex items-center gap-2">
                      {lab.title}
                      {!isUnlocked && <span style={{ color: '#a1a1aa' }}>🔒</span>}
                    </h4>
                    <div className="flex gap-2 mt-1">
                      <span 
                        className="badge" 
                        style={{ 
                          fontSize: '10px',
                          background: lab.isFreeAccess ? '#22c55e20' : (isUnlocked ? '#3b82f620' : 'rgba(255,255,255,0.04)'),
                          color: lab.isFreeAccess ? '#22c55e' : (isUnlocked ? '#3b82f6' : '#a1a1aa')
                        }}
                      >
                        {lab.isFreeAccess ? '🆓 FREE' : (isUnlocked ? '🔓 UNLOCKED' : (lab.status === 'coming-soon' ? '🚀 Q1 2026' : '🔒 LOCKED'))}
                      </span>
                      <span 
                        className="badge" 
                        style={{ 
                          fontSize: '10px',
                          background: lab.difficulty === 'Expert' ? '#7c3aed20' : 
                                     lab.difficulty === 'Advanced' ? '#ef444420' : '#f59e0b20',
                          color: lab.difficulty === 'Expert' ? '#7c3aed' : 
                                lab.difficulty === 'Advanced' ? '#ef4444' : '#f59e0b'
                        }}
                      >
                        {lab.difficulty}
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-muted mb-4" style={{ 
                  display: '-webkit-box', 
                  WebkitLineClamp: 2, 
                  WebkitBoxOrient: 'vertical', 
                  overflow: 'hidden' 
                }}>
                  {lab.description}
                </p>

                {isUnlocked ? (
                  <button 
                    className="btn btn-primary btn-full" 
                    style={{ background: lab.color }}
                    onClick={() => handleLaunchLab(lab)}
                  >
                    <Play size={16} />
                    {lab.status === 'active' ? 'Launch Lab' : 'Preview'}
                  </button>
                ) : canGenerate ? (
                  <button 
                    className="btn btn-secondary btn-full"
                    onClick={() => {
                      const enrollment = lab.userAccess?.grantingEnrollments?.[0];
                      if (enrollment) handleGenerateKey(lab, enrollment._id);
                    }}
                    disabled={generatingLabKey === lab.labId}
                  >
                    {generatingLabKey === lab.labId ? (
                      <>Generating...</>
                    ) : (
                      <>🔑 Generate Access Key</>
                    )}
                  </button>
                ) : (
                  <button 
                    className="btn btn-full"
                    style={{ background: 'rgba(255,255,255,0.04)', color: '#71717a', border: '1px solid rgba(255,255,255,0.08)', cursor: 'default' }}
                    disabled
                  >
                    {lab.status === 'coming-soon' ? 'Coming Q1 2026' : 'Enroll to Unlock'}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Browse all labs link */}
        <div className="portal-card mt-4" style={{ textAlign: 'center', padding: '24px' }}>
          <p className="text-muted mb-3">
            Explore our full catalog of engineering and physics virtual laboratories
          </p>
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/virtuallabs')}
          >
            <FlaskConical size={16} />
            Browse All Virtual Labs
          </button>
        </div>
      </div>
    );
  };

  const renderAssignments = () => (
    <div className="tab-content">
      <StudentPortalTabHeader
        icon={<FileText size={20} />}
        title={TAB_META.assignments.title}
        subtitle={TAB_META.assignments.subtitle}
        actions={(
          <button className="btn btn-secondary btn-sm">
            <Filter size={16} />
            Filter
          </button>
        )}
      />

      {renderEmptyRich(
        '📝',
        'Assignments land here',
        'When instructors publish homework or quizzes, you\'ll get due dates, status, and submission links in one queue.',
        <button type="button" className="btn btn-primary" onClick={() => handleTabChange('courses')}>View my courses</button>,
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/courses')}>Enroll in a course</button>
      )}
    </div>
  );

  const renderSchedule = () => (
    <div className="tab-content">
      <StudentPortalTabHeader
        icon={<Calendar size={20} />}
        title={TAB_META.schedule.title}
        subtitle={TAB_META.schedule.subtitle}
        actions={(
          <button className="btn btn-primary btn-sm">
            <Plus size={16} />
            Add Event
          </button>
        )}
      />

      {renderEmptyRich(
        '📅',
        'Plan your learning rhythm',
        'Block study time, lab sessions, and mentor calls — your calendar keeps momentum visible.',
        <button type="button" className="btn btn-primary" onClick={() => navigate('/qoreconnect/mentorship')}>Book a mentor</button>,
        <button type="button" className="btn btn-secondary" onClick={() => handleTabChange('dashboard')}>Back to dashboard</button>
      )}
    </div>
  );

  const renderProgress = () => (
    <div className="tab-content">
      <StudentPortalTabHeader
        icon={<BarChart3 size={20} />}
        title={TAB_META.progress.title}
        subtitle={TAB_META.progress.subtitle}
        actions={(
          <button className="btn btn-secondary btn-sm">
            <Download size={16} />
            Export
          </button>
        )}
      />

      {/* Progress Stats */}
      <div className="stats-grid mb-6">
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">Avg Progress</span>
            <TrendingUp size={18} color="#3b82f6" />
          </div>
          <div className="stat-value">{stats.averageProgress}%</div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">Completed</span>
            <CheckCircle size={18} color="#10b981" />
          </div>
          <div className="stat-value">{stats.coursesCompleted}</div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">Day Streak</span>
            <Zap size={18} color="#f59e0b" />
          </div>
          <div className="stat-value">{stats.currentStreak}</div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">Total Hours</span>
            <Clock size={18} color="#8b5cf6" />
          </div>
          <div className="stat-value">{stats.totalHours}h</div>
        </div>
      </div>

      {/* Course Progress List */}
      <div className="portal-card">
        <h3 className="card-title mb-4">Course Progress</h3>
        {enrollments.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📊</div>
            <h3 className="empty-title">No Progress Data</h3>
            <p className="empty-description">Start learning to see your progress</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {enrollments.map(enrollment => {
              const progress = getProgress(enrollment);
              const color = pickColor(enrollment.course?.category || enrollment.course?.title || '');

              return (
                <div key={enrollment._id} className="portal-card" style={{ padding: '16px' }}>
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-semibold">{enrollment.course?.title}</h4>
                    <span className="font-bold" style={{ color }}>{progress}%</span>
                  </div>
                  <div className="progress-bar mb-3">
                    <div className="progress-fill" style={{ width: `${progress}%`, background: color }} />
                  </div>
                  <div className="flex justify-between text-sm text-muted">
                    <span>{enrollment.progress?.completedLessons?.length || 0} lessons done</span>
                    <span>{enrollment.analytics?.totalVideoWatched || 0}h watched</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  const renderAchievements = () => {
    const achievements = [
      {
        id: 1,
        title: 'First Course',
        description: 'Complete your first course',
        icon: <BookOpen size={28} />,
        color: '#3b82f6',
        earned: stats.coursesCompleted > 0
      },
      {
        id: 2,
        title: 'Perfect Streak',
        description: '7-day learning streak',
        icon: <Zap size={28} />,
        color: '#f59e0b',
        earned: stats.currentStreak >= 7
      },
      {
        id: 3,
        title: 'Lab Expert',
        description: 'Complete 5 virtual labs',
        icon: <FlaskConical size={28} />,
        color: '#8b5cf6',
        earned: false
      },
      {
        id: 4,
        title: 'AI Pioneer',
        description: 'Complete an AI course',
        icon: <Brain size={28} />,
        color: '#ef4444',
        earned: false
      },
      {
        id: 5,
        title: 'Team Player',
        description: 'Join 5 study groups',
        icon: <Users size={28} />,
        color: '#06b6d4',
        earned: false
      },
      {
        id: 6,
        title: 'Rising Star',
        description: 'Earn 1000 points',
        icon: <Star size={28} />,
        color: '#10b981',
        earned: stats.achievementPoints >= 1000
      }
    ];

    return (
      <div className="tab-content">
        <StudentPortalTabHeader
          icon={<Trophy size={20} />}
          title={TAB_META.achievements.title}
          subtitle={TAB_META.achievements.subtitle}
          actions={<span className="badge badge-warning">{stats.achievementPoints} points</span>}
        />

        <div className="courses-grid">
          {achievements.map(achievement => (
            <div 
              key={achievement.id} 
              className="portal-card"
              style={{ 
                opacity: achievement.earned ? 1 : 0.5,
                borderColor: achievement.earned ? achievement.color : undefined
              }}
            >
              <div className="text-center">
                <div 
                  style={{
                    width: '80px',
                    height: '80px',
                    margin: '0 auto 16px',
                    borderRadius: '50%',
                    background: `${achievement.color}20`,
                    color: achievement.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                  }}
                >
                  {achievement.icon}
                  {achievement.earned && (
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      background: '#10b981',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <CheckCircle size={14} color="white" />
                    </div>
                  )}
                </div>
                <h4 className="font-semibold mb-1">{achievement.title}</h4>
                <p className="text-sm text-muted mb-3">{achievement.description}</p>
                <span className={`badge ${achievement.earned ? 'badge-success' : 'badge-default'}`}>
                  {achievement.earned ? 'Earned' : 'Locked'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderMessages = () => (
    <div className="tab-content">
      <StudentPortalTabHeader
        icon={<MessageCircle size={20} />}
        title={TAB_META.messages.title}
        subtitle={TAB_META.messages.subtitle}
        actions={<span className="badge badge-error">0 unread</span>}
      />

      {renderEmptyRich(
        '💬',
        'Inbox is quiet — for now',
        'Instructor feedback, cohort threads, and mentor notes will show up here. Meanwhile, Qore AI is always on.',
        <button type="button" className="btn btn-primary" onClick={() => navigate('/aitutoring')}>Open Qore AI</button>,
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/qoreconnect/forum')}>Visit forum</button>
      )}
    </div>
  );

  const renderResources = () => (
    <div className="tab-content">
      <StudentPortalTabHeader
        icon={<Layers size={20} />}
        title={TAB_META.resources.title}
        subtitle={TAB_META.resources.subtitle}
        actions={(
          <button className="btn btn-secondary btn-sm">
            <Filter size={16} />
            Filter
          </button>
        )}
      />

      {renderEmptyRich(
        '📂',
        'Resource library',
        'Cheatsheets, lab guides, and course downloads will collect here as you progress.',
        <button type="button" className="btn btn-primary" onClick={() => navigate('/courses')}>Explore courses</button>,
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/virtuallabs')}>Open virtual labs</button>
      )}
    </div>
  );

  // ─── CERTIFICATES TAB ────────────────────────────────────────────────────────
  const renderCertificates = () => <CertificatesTab token={token} navigate={navigate} />;

  const renderLearningPath = () => (
    <div className="tab-content">
      <StudentPortalTabHeader
        icon={<Target size={20} />}
        title={TAB_META.learningPath.title}
        subtitle={TAB_META.learningPath.subtitle}
        actions={(
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/create-path')}>
            <Plus size={16} />
            Create Path
          </button>
        )}
      />

      {renderEmptyRich(
        '🎯',
        'Design your runway',
        'Tell us your target role — we\'ll sequence courses, labs, and milestones into a path that adapts as you grow.',
        <button type="button" className="btn btn-primary" onClick={() => navigate('/create-path')}>
          <Rocket size={16} />
          Create my path
        </button>,
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/aitutoring')}>Ask Qore AI</button>
      )}
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return renderDashboard();
      case 'courses': return renderCourses();
      case 'labs': return renderLabs();
      case 'assignments': return renderAssignments();
      case 'schedule': return renderSchedule();
      case 'progress': return renderProgress();
      case 'achievements': return renderAchievements();
      case 'certificates': return renderCertificates();
      case 'messages': return renderMessages();
      case 'resources': return renderResources();
      case 'learningPath': return renderLearningPath();
      case 'settings': return (
        <StudentPortalSettings
          profile={profile}
          token={token}
          onProfileUpdated={handleProfileUpdated}
          onLogout={logout}
        />
      );
      default: return renderDashboard();
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="sp-shell">
        <div className="sp-ambient" aria-hidden>
          <div className="sp-ambient__orb sp-ambient__orb--violet" />
          <div className="sp-ambient__orb sp-ambient__orb--cyan" />
        </div>
        <div className="sp-loading">
          <div className="sp-loading__hero" />
          <div className="sp-loading__grid">
            {[1, 2, 3, 4].map((i) => <div key={i} className="sp-loading__card" />)}
          </div>
          <div className="sp-loading__panel" />
        </div>
      </div>
    );
  }

  const quickTabs = [
    { id: 'dashboard', label: 'Home' },
    { id: 'courses', label: 'Courses' },
    { id: 'learningPath', label: 'Path' },
    { id: 'labs', label: 'Labs' },
    { id: 'progress', label: 'Progress' },
  ];

  const handleContinueFocus = () => {
    if (focusEnrollment?.course?._id) {
      navigate(`/studentportal/mycourses/${focusEnrollment.course._id}`);
    } else {
      navigate('/courses');
    }
  };

  return (
    <StudentPortalShell
      studentInfo={studentInfo}
      stats={stats}
      activeTab={activeTab}
      activeTabMeta={activeTabMeta}
      navGroups={navGroups}
      quickTabs={quickTabs}
      onTabChange={handleTabChange}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      sidebarCollapsed={sidebarCollapsed}
      onToggleCollapse={() => setSidebarCollapsed((c) => !c)}
      sidebarOpen={sidebarOpen}
      onToggleSidebar={() => setSidebarOpen((o) => !o)}
      isMobile={isMobile}
      onLogout={logout}
      onOpenSettings={() => handleTabChange('settings')}
      onRefresh={refresh}
      error={error}
      showContinueFab={Boolean(focusEnrollment)}
      onContinueFocus={handleContinueFocus}
    >
      {renderContent()}
    </StudentPortalShell>
  );
};

// Helper function for AI context
function getPageContext(tabId) {
  const contexts = {
    dashboard: 'Viewing the main dashboard with learning progress overview',
    courses: 'Browsing enrolled courses and progress',
    labs: 'Exploring virtual labs for hands-on practice',
    assignments: 'Reviewing assignments and homework',
    schedule: 'Checking learning schedule and events',
    progress: 'Analyzing detailed learning progress and analytics',
    achievements: 'Viewing earned badges and achievements',
    messages: 'Reading messages from instructors and groups',
    resources: 'Browsing learning resources and materials',
    learningPath: 'Managing AI-powered personalized learning path',
    settings: 'Managing account settings and preferences',
  };
  return contexts[tabId] || 'Navigating the student portal';
}

export default StudentPortal;
